import { DropdownItemProps } from "semantic-ui-react";
import { TranslateProps } from "../i18n/TranslateProps";
import { IAuthorization } from "./authorization";
import { IRequestFeedback } from "./requestFeedback";

export interface ICertificate {
    guid: string;
    lastUpdated: string;
    type: CertificateType;
    qualificationID: string;
    restriction96: string;
    status: CertificateStatus;
    startDate: string;
    endDate: string;
    comments: string;
    crewMemberGuid: string;
    suspensionStartDate: string;
    suspensionEndDate: string;
    restrictions: number[];
    authorizations: IAuthorization[];
    limitedToOneCraftDescription: string;
    limitedAreaDescription: string;
    limitedTaskDescription: string;
    issuingAuthority: string;
    fitnessIssueDate: string;
    fitnessValidUntil: string;
    administrativeNumber: string;
    lastQualificationID: string;
    nameLineOne: string;
    nameLineTwo: string;
    zipCode: string;
    city: string;
    street: string;
    country: number;
    gender: number;
    costBearer: number;
    requestFeedbacks: IRequestFeedback[];
    ecdbAcknowledged: boolean;
    batchId: string;
    printAcknowledged: boolean;
    printFeedback: string;
    // ICC additional inputs
    vehicleLength: number;
    loadCapacity: number;
    carryingCapacityValue: string;
    drivePower: number;
    drivePowerValue: string;
    validFor: string;
    conditions?: number[];
    // Boatmaster AT additional inputs
    localScope: number;
    localScopeValue: string;
    certificateNotes: number[];
    notesValue: string;
    vehicleType: string;
    passengers: number;
    radar: boolean;
    vehicleLengthBoatmaster: boolean;
    materialScope: string;
    riverKilometers: number[];
    // ICS additional inputs
    businessNumber?: string;
    applicationCategory: ApplicationCategory;
    applicationStatus: ApplicationStatus;
    canceled: boolean;
    examinationOrganization: string;
    firstAidCourse: string;
    dateOfReceipt: string;
    dateOfPayment: string;
    paymentAmount?: string;
    paymentType: string;
    outgoingDate: string;
    dateOfIssue: string;
    processor: string;
    stateOfBirth: number;
    nameOfAttachment1?: string;
    nameOfAttachment2?: string;
}

export interface ICondition {
    type: number;
}

export const newCertificateInit: ICertificate = {
    guid: "",
    lastUpdated: "",
    type: -1,
    qualificationID: "",
    restriction96: "",
    status: -1,
    startDate: "",
    endDate: "",
    comments: "",
    crewMemberGuid: "",
    suspensionStartDate: "",
    suspensionEndDate: "",
    restrictions: [],
    authorizations: [],
    limitedToOneCraftDescription: "",
    limitedAreaDescription: "",
    limitedTaskDescription: "",
    issuingAuthority: "",
    fitnessIssueDate: "",
    fitnessValidUntil: "",
    administrativeNumber: "",
    lastQualificationID: "",
    nameLineOne: "",
    nameLineTwo: "",
    zipCode: "",
    city: "",
    street: "",
    country: 9,
    gender: 0,
    vehicleLength: -1,
    loadCapacity: -1,
    carryingCapacityValue: "",
    drivePower: -1,
    drivePowerValue: "",
    validFor: "",
    conditions: [],
    localScope: 0,
    localScopeValue: "",
    certificateNotes: [],
    notesValue: "",
    vehicleType: "",
    passengers: 0,
    radar: false,
    vehicleLengthBoatmaster: false,
    materialScope: "",
    riverKilometers: [],
    costBearer: 2,
    requestFeedbacks: [],
    ecdbAcknowledged: false,
    batchId: "",
    printAcknowledged: false,
    printFeedback: "",
    applicationCategory: 0,
    applicationStatus: 0,
    canceled: false,
    examinationOrganization: "",
    firstAidCourse: "",
    dateOfReceipt: "",
    dateOfPayment: "",
    paymentType: "",
    outgoingDate: "",
    dateOfIssue: "",
    processor: "",
    stateOfBirth: -1,
};

export enum CertificateType {
    UcqBoatmaster = 1,
    UcqHelmsman = 2,
    UcqAbleBoatman = 3,
    UcqBoatman = 4,
    UcqDeckhand = 5,
    UcqApprentice = 6,
    UcqLngExpert = 50,
    UcqPassengerNavigationExpert = 51,
    BoatmastersCertificateAt = 60,
    InternationalCertificateForOperatorsOfPleasureCraft = 61,
    CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt = 62,
    EngineersCertificate = 63,
    InternationalCertificateForOperatorsOfPleasureCraftICS = 64,
}

export const certificateTypeOptions: DropdownItemProps[] = [
    {
        key: CertificateType.UcqBoatmaster,
        text: "ucqBoatmaster",
        value: CertificateType.UcqBoatmaster,
    },
    {
        key: CertificateType.UcqHelmsman,
        text: "ucqHelmsman",
        value: CertificateType.UcqHelmsman,
    },
    {
        key: CertificateType.UcqAbleBoatman,
        text: "ucqAbleBoatman",
        value: CertificateType.UcqAbleBoatman,
    },
    {
        key: CertificateType.UcqBoatman,
        text: "ucqBoatman",
        value: CertificateType.UcqBoatman,
    },
    {
        key: CertificateType.UcqDeckhand,
        text: "ucqDeckhand",
        value: CertificateType.UcqDeckhand,
    },
    {
        key: CertificateType.UcqApprentice,
        text: "ucqApprentice",
        value: CertificateType.UcqApprentice,
    },
    {
        key: CertificateType.UcqLngExpert,
        text: "ucqLngExpert",
        value: CertificateType.UcqLngExpert,
    },
    {
        key: CertificateType.UcqPassengerNavigationExpert,
        text: "ucqPassengerExpert",
        value: CertificateType.UcqPassengerNavigationExpert,
    },
    {
        key: CertificateType.BoatmastersCertificateAt,
        text: "boatmasterAt",
        value: CertificateType.BoatmastersCertificateAt,
    },
    {
        key: CertificateType.InternationalCertificateForOperatorsOfPleasureCraft,
        text: "pleasureCraft",
        value: CertificateType.InternationalCertificateForOperatorsOfPleasureCraft,
    },
    {
        key: CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt,
        text: "certificateStretches",
        value: CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt,
    },
    {
        key: CertificateType.EngineersCertificate,
        text: "engineersCertificate",
        value: CertificateType.EngineersCertificate,
    },
    {
        key: CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS,
        text: "pleasureCraftICS",
        value: CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS,
    },
];

export const statePrintingCertificates = [
    CertificateType.UcqBoatmaster,
    CertificateType.UcqLngExpert,
    CertificateType.UcqPassengerNavigationExpert,
    CertificateType.BoatmastersCertificateAt,
    CertificateType.InternationalCertificateForOperatorsOfPleasureCraft,
    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS,
    CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt,
];

export const unionCertificates = [
    CertificateType.UcqBoatmaster,
    CertificateType.UcqLngExpert,
    CertificateType.UcqPassengerNavigationExpert,
    CertificateType.UcqDeckhand,
    CertificateType.UcqApprentice,
    CertificateType.UcqBoatman,
    CertificateType.UcqAbleBoatman,
    CertificateType.UcqHelmsman,
];

export enum CertificateStatus {
    Active = 1,
    Expired = 10,
    Suspended = 11,
    Withdrawn = 12,
    Lost = 90,
    Stolen = 91,
    Destroyed = 92,
}

export enum ApplicationCategory {
    NewIssue,
    After2012,
    Before2012,
}

export enum ApplicationStatus {
    InProcess,
    SentToPrint,
    Incorrect,
    Completed,
}

export const aplicationStatusOptions: DropdownItemProps[] = [
    { key: ApplicationStatus.InProcess, text: "inprocess", value: ApplicationStatus.InProcess },
    {
        key: ApplicationStatus.SentToPrint,
        text: "sentToPrint",
        value: ApplicationStatus.SentToPrint,
    },
    { key: ApplicationStatus.Incorrect, text: "incorrect", value: ApplicationStatus.Incorrect },
    { key: ApplicationStatus.Completed, text: "completed", value: ApplicationStatus.Completed },
];
export const certificateStatusOptions: DropdownItemProps[] = [
    { key: CertificateStatus.Active, text: "active", value: CertificateStatus.Active },
    { key: CertificateStatus.Destroyed, text: "destroyed", value: CertificateStatus.Destroyed },
    { key: CertificateStatus.Expired, text: "expired", value: CertificateStatus.Expired },
    { key: CertificateStatus.Lost, text: "lost", value: CertificateStatus.Lost },
    { key: CertificateStatus.Stolen, text: "stolen", value: CertificateStatus.Stolen },
    { key: CertificateStatus.Suspended, text: "suspended", value: CertificateStatus.Suspended },
    { key: CertificateStatus.Withdrawn, text: "withdrawn", value: CertificateStatus.Withdrawn },
];

export const restrictionStatusOptions: DropdownItemProps[] = [
    { key: 1, text: "sightCorrection", value: 1 },
    { key: 2, text: "hearingAidRequired", value: 2 },
    { key: 3, text: "limbProsthesis", value: 3 },
    { key: 4, text: "noSoloDuty", value: 4 },
    { key: 5, text: "onlyDuringDaylight", value: 5 },
    { key: 6, text: "noNavigationalDuties", value: 6 },
    { key: 7, text: "limitedToOneCraft", value: 7 },
    { key: 8, text: "limitedArea", value: 8 },
    { key: 9, text: "limitedTask", value: 9 },
];

export const modifyCertificateInit: Partial<ICertificate> = {
    status: -1,
    suspensionStartDate: "",
    suspensionEndDate: "",
    comments: "",
};

export const loadCapacityOptions: DropdownItemProps[] = [
    { key: 0, text: "< t", value: 0 },
    { key: 1, text: "≤ t", value: 1 },
];

export const drivePowerOptions: DropdownItemProps[] = [
    { key: 0, text: "< KW", value: 0 },
    { key: 1, text: "≤ KW", value: 1 },
];

export const applicationCategoryOptions: DropdownItemProps[] = [
    { key: 0, text: "newIssueICS", value: 0 },
    { key: 1, text: "duplicateMod2012", value: 1 },
    { key: 2, text: "duplicateBefore2012", value: 2 },
];

export const vehicleLengthOptions: DropdownItemProps[] = [
    { key: 8, text: "no limits", value: 8 },
    { key: 0, text: "< 30 m", value: 0 },
    { key: 1, text: "< 20 m", value: 1 },
    { key: 2, text: "≤ 10 m", value: 2 },
    { key: 3, text: "< 9 m", value: 3 },
    { key: 4, text: "< 8 m", value: 4 },
    { key: 5, text: "< 7 m", value: 5 },
    { key: 6, text: "< 6 m", value: 6 },
    { key: 7, text: "< 5 m", value: 7 },
];

export const localScopeOptions: DropdownItemProps[] = [
    {
        key: 0,
        text: "waterwaysAndOther",
        value: 0,
        content: "Wasserstraßen und sonstige Binnengewässer",
    },
    { key: 1, text: "waterways", value: 1, content: "Wasserstraßen" },
];

export const certificateNotesOptions: DropdownItemProps[] = [
    { key: 0, text: "visualAid", value: 0, content: "Sehhilfe" },
    { key: 1, text: "hearingAid", value: 1, content: "Hörhilfe" },
];

export const passengersOptions: DropdownItemProps[] = [
    { key: 0, text: "noPassengers", value: 0, content: "Keine Fahrgäste" },
    { key: 1, text: "lessThanPassengers", value: 1, content: "Weniger als 12 Fahrgäste" },
    { key: 2, text: "passengers", value: 2, content: "Fahrgäste" },
];

export const costBearerOptions: DropdownItemProps[] = [
    { key: 0, text: "statePrintingOffice", value: 0 },
    { key: 1, text: "deliveryAgent", value: 1 },
    { key: 2, text: "authority", value: 2 },
];

export const conditionOptions: DropdownItemProps[] = [
    { key: 0, text: "visualAidTypeEN", value: 0 },
    { key: 1, text: "hearingAidTypeEN", value: 1 },
    { key: 2, text: "limbProsthesisType", value: 2 },
    { key: 3, text: "onlyDuringDaylightType", value: 3 },
    { key: 4, text: "eyeProtection", value: 4 },
    { key: 5, text: "cushion", value: 5 },
];

export const getCertificateStatus = (cert: ICertificate) => {
    if (cert.type === CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS) {
        return getICSCertificateStatus(cert.canceled, cert.applicationStatus, cert.status);
    } else {
        return TranslateProps(certificateStatusOptions).find(x => x.value === cert.status)?.text;
    }
};

export const getICSCertificateStatus = (
    canceled: boolean,
    appStatus: ApplicationStatus,
    certStatus: CertificateStatus
) => {
    if (canceled) {
        return TranslateProps([{ key: "canceled", text: "canceled", value: -1 }]).find(
            x => x.value === -1
        )?.text;
    } else if (
        appStatus !== ApplicationStatus.Completed ||
        certStatus === CertificateStatus.Active
    ) {
        return TranslateProps(aplicationStatusOptions).find(x => x.value === appStatus)?.text;
    } else {
        return TranslateProps(certificateStatusOptions).find(x => x.value === certStatus)?.text;
    }
};

export interface IcsAplication {
    qualificationID?: string;
    administrativeNumber: string;
    lastName?: string;
    firstName?: string;
    areaOfValidity?: string;
    examinationOrganization?: string;
    dateOfReceipt?: string;
    dateOfIssue?: string;
    comments?: string;
    applicationStatus?: ApplicationStatus;
    status?: CertificateStatus;
    userGuid: string;
    canceled: boolean;
}

export interface SearchableIcs {
    readonly value: string;
    readonly text: string;
    readonly key: string;
}

export enum ConditionType {
    visualAidTypeEN = 0,
    hearingAidTypeEN = 1,
    limbProsthesisType = 2,
    onlyDuringDaylightType = 3,
    eyeProtection = 4,
    cushion = 5,
}
